'use client';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';

import { useUserSubscriptionFetcher } from '@/app/_hooks/fetchers/userSubscription';
import { useUser } from '@auth0/nextjs-auth0/client';
import { UserSubscription } from '@/app/_lib/interfaces';
import { PlanDetails } from '@/app/_components/subscription/PlanDetails';

interface UserSubscriptionContextType {
  userSubscription: UserSubscription | undefined;
  isLoading: boolean;
  showSubscriptionDialog: boolean;
  revalidate: () => void;
  setShowSubscriptionDialog: (
    value: boolean,
    closeDialogCallback?: () => void
  ) => void;
}

export const UserSubscriptionContext =
  createContext<UserSubscriptionContextType>({
    userSubscription: undefined,
    isLoading: false,
    showSubscriptionDialog: false,
    revalidate: () => undefined,
    setShowSubscriptionDialog: () => undefined,
  });

const UserSubscriptionProvider: React.FC<PropsWithChildren> = ({
  children,
}: PropsWithChildren) => {
  const { user } = useUser();
  const {
    data: userSubscription,
    isLoading,
    mutate: revalidate,
  } = useUserSubscriptionFetcher(`${user?.sub}`, !user);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [closeDialogCallback, setCloseDialogCallback] = useState<
    (() => void) | undefined
  >(undefined);

  const handleDialogOpen = useCallback(
    (value: boolean, callback?: () => void) => {
      if (!value && closeDialogCallback) {
        closeDialogCallback();
        setCloseDialogCallback(undefined);
      }
      setShowSubscriptionDialog(value);
      if (callback) {
        setCloseDialogCallback(() => callback);
      }
    },
    [closeDialogCallback]
  );

  return (
    <UserSubscriptionContext.Provider
      value={{
        userSubscription,
        isLoading,
        revalidate,
        showSubscriptionDialog,
        setShowSubscriptionDialog: handleDialogOpen,
      }}
    >
      {children}

      {userSubscription && (
        <PlanDetails
          isOpen={showSubscriptionDialog}
          handleOpen={handleDialogOpen}
          userSubscription={userSubscription}
        />
      )}
    </UserSubscriptionContext.Provider>
  );
};

const useUserSubscription = () => {
  const context = useContext(UserSubscriptionContext);
  if (!context) {
    throw new Error(
      `useUserSubscription must be used within a UserSubscriptionProvider`
    );
  }
  return context;
};

export default UserSubscriptionProvider;

export { useUserSubscription };
